/** @fileoverview Renders chat actions for history, new conversations, and preview. */

import { History, MessageSquare, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import ChatFilesPopover from './ChatFilesPopover';
import type { ChatHeaderProps } from './types';

/**
 * Chat toolbar; drawn as a bar matching the live preview's header while the preview is open.
 * @param props.title - saved conversation title, if any
 * @param props.historyView - whether the history list is showing
 * @param props.canStartNew - whether to offer the new conversation button
 * @param props.previewOpen - whether the live preview pane is open
 * @param props.conversationFiles - files in this chat; the popover is hidden when there are none
 * @param props.onToggleFile - switches a file on or off
 * @param props.onDeleteFile - deletes a sent file everywhere; resolves true on success
 * @param props.onHistoryToggle - shows or hides the history list
 * @param props.onNewConversation - starts a new conversation
 */
export default function ChatHeader({
  title,
  historyView,
  canStartNew,
  previewOpen,
  conversationFiles,
  onToggleFile,
  onDeleteFile,
  onHistoryToggle,
  onNewConversation,
}: ChatHeaderProps) {
  // Full width, an empty chat needs no label; beside the preview it balances the "Live preview" label.
  const label = title || (previewOpen ? 'New chat' : '');
  return (
    <header
      className={`flex h-10 shrink-0 items-center gap-2 ${
        previewOpen ? 'border-b border-app-border bg-app-surface-card px-3' : 'px-4'
      }`}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2 text-xs text-app-text-muted">
        {label && (
          <>
            <MessageSquare
              className="h-3.5 w-3.5 shrink-0 text-app-brand-strong"
              aria-hidden="true"
            />
            <span className="truncate" title={label}>
              {label}
            </span>
          </>
        )}
      </span>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          className={`h-7 gap-1.5 px-2 text-xs font-medium hover:bg-app-surface-muted-strong hover:text-app-text ${
            historyView ? 'bg-app-surface-muted-strong text-app-text' : 'text-app-text-muted'
          }`}
          aria-pressed={historyView}
          onClick={onHistoryToggle}
        >
          <History className="h-4 w-4" aria-hidden="true" />
          History
        </Button>
        <ChatFilesPopover
          data={conversationFiles}
          onToggle={onToggleFile}
          onDelete={onDeleteFile}
        />
        {canStartNew && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-app-text-muted hover:bg-app-surface-muted-strong hover:text-app-text"
            aria-label="New conversation"
            title="New conversation"
            onClick={onNewConversation}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>
    </header>
  );
}
